import {
  boundsToMapboxCoordinates,
  getApprovedManualBounds,
  validateRadarBounds,
} from "./siataSantaElenaRadarBoundsUtils.js";
import {
  SIATA_SANTA_ELENA_CALIBRATION_VERSION,
  SIATA_SANTA_ELENA_GEOREF_SOURCES,
} from "../constants/siataSantaElenaCalibrationConstants.js";
import { SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG } from "../components/weather/radar/siataSantaElenaRadarOverlay.js";

const STORAGE_KEY = "midpoint_siata_radar_calibration_v1";
const CHANGE_EVENT = "midpoint:siata-radar-calibration-change";

function readStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStorage(state) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: state }));
}

function createDefaultState(serverGeoref = null) {
  const configuredBounds =
    serverGeoref?.boundsValid && serverGeoref?.bounds
      ? serverGeoref.bounds
      : getApprovedManualBounds();

  return {
    active: false,
    previewBounds: configuredBounds,
    configuredBounds,
    serverGeoref: serverGeoref || null,
    geotiffBounds: serverGeoref?.geotiff?.extraction?.bounds || null,
    approvedBounds: getApprovedManualBounds(),
    opacity: SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG.defaultOpacity,
    showBoundsOutline: false,
    showReferenceMarkers: false,
    nudgeStep: 0.01,
    checklist: {},
    calibrationVersion:
      serverGeoref?.calibrationVersion || SIATA_SANTA_ELENA_CALIBRATION_VERSION,
    notes: "",
    activeGeorefSource: serverGeoref?.activeGeorefSource || null,
    confidence: serverGeoref?.confidence || "medium",
  };
}

export function getSiataCalibrationState(serverGeoref = null) {
  const stored = readStorage();
  const defaults = createDefaultState(serverGeoref);

  if (!stored) {
    return defaults;
  }

  return {
    ...defaults,
    ...stored,
    configuredBounds: defaults.configuredBounds,
    previewBounds: stored.previewBounds || defaults.previewBounds,
    serverGeoref: serverGeoref || stored.serverGeoref || null,
  };
}

export function setSiataCalibrationState(patch = {}) {
  const current = getSiataCalibrationState();
  const next = { ...current, ...patch };

  if (patch.previewBounds) {
    const validation = validateRadarBounds(patch.previewBounds);
    if (!validation.valid) {
      return { ok: false, error: validation.message, state: current };
    }
  }

  writeStorage(next);
  return { ok: true, state: next };
}

export function resetPreviewToGeotiffBounds(state) {
  const geotiffBounds = state?.serverGeoref?.geotiff?.extraction?.bounds || state?.geotiffBounds;
  if (!geotiffBounds || !validateRadarBounds(geotiffBounds).valid) {
    return { ok: false, error: "GeoTIFF bounds are not available.", state };
  }
  return setSiataCalibrationState({ previewBounds: geotiffBounds });
}

export function resetPreviewToApprovedBounds(state) {
  const approved = state?.approvedBounds || getApprovedManualBounds();
  return setSiataCalibrationState({ previewBounds: approved });
}

export function buildSiataCalibrationDiagnostics(state) {
  const bounds = state?.previewBounds || state?.configuredBounds;
  return {
    activeGeorefSource: state?.activeGeorefSource,
    confidence: state?.confidence,
    calibrationVersion: state?.calibrationVersion,
    bounds,
    mapboxCoordinates: boundsToMapboxCoordinates(bounds),
    showBoundsOutline: state?.showBoundsOutline,
    showReferenceMarkers: state?.showReferenceMarkers,
    nudgeStep: state?.nudgeStep,
    candidateSource: SIATA_SANTA_ELENA_GEOREF_SOURCES.CANDIDATE_PREVIEW,
    checklist: state?.checklist,
    notes: state?.notes,
  };
}

export function subscribeSiataCalibrationChange(handler) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const listener = (event) => handler(event.detail);
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}
